import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    GenerateBranchApiTokenResponse,
    GetBranchApiTokenResponse,
} from '@/api/handlers/resources/branches/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { BranchQueryKeys } from './branchQueryKeys';

type ApiTokenCache = ApiResponse<GetBranchApiTokenResponse> | undefined;

/**
 * Generates (or rotates) a branch's POS API token. On success:
 *
 * 1. Invalidate the `/details` pages so the table chip flips to "Token set"
 *    on refetch (the list is server-paginated, so we can't reliably patch a
 *    single row in place).
 * 2. Patch the per-branch `apiToken` query with the new token's preview and
 *    `setAt`, so the modal identifies the token it just minted without a
 *    round-trip. The plaintext is deliberately *not* cached: it is disclosed
 *    once, and a copy kept anywhere the UI can read again defeats the point
 *    of not storing it server-side (BOS-118).
 */
export const useGenerateBranchApiTokenMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GenerateBranchApiTokenResponse>,
        Error,
        string
    >({
        mutationFn: (branchId) => apiClient.branches.generateApiToken(branchId),
        onSuccess: (result, branchId) => {
            if (!result.success) {
                return;
            }
            queryClient.invalidateQueries({
                queryKey: BranchQueryKeys.details(),
            });
            const patched: ApiResponse<GetBranchApiTokenResponse> = {
                success: true,
                response: {
                    branchId: result.response.branchId,
                    tokenPreview: result.response.token.slice(-4),
                    setAt: result.response.setAt,
                },
            };
            queryClient.setQueryData<ApiTokenCache>(
                BranchQueryKeys.apiToken(branchId),
                patched,
            );
        },
    });
};
